import React from 'react';
import {
  CheckCircle2,
  Circle,
  FileCheck2,
  Loader2,
  MapPinned,
  Save,
  ShieldCheck,
} from 'lucide-react';

import { Button } from '../../ui/button';
import type {
  TestFitWorkflowCode,
  TestFitWorkflowReadiness,
} from '../../../lib/generationImpact/testFitWorkflow';

interface Props {
  readiness: TestFitWorkflowReadiness;
  readOnly: boolean;
  busy: boolean;
  saving: boolean;
  promoting: boolean;
  publishing: boolean;
  onSave: () => void;
  onPromote: () => void;
  onReviewPublish: () => void;
  onRebuildSed: () => void;
  onGenerateTestFit?: () => void;
  /**
   * Non-null when publishing itself is blocked for a reason the readiness code
   * cannot express (no layout anchor, unsaved edits, remote drift). The publish
   * CTA consulted only `readOnly || busy`, so it stayed enabled, opened the
   * review dialog, and left the user staring at a greyed-out Publish button
   * with the explanation rendered on a card BEHIND the dialog.
   */
  publishBlockReason?: string | null;
  /**
   * A way to reach the blocker's remedy when it lives on ANOTHER step. Save,
   * reload and promote all have their control on this panel; the layout anchor
   * does not, so naming that blocker without a route to it stranded the user.
   */
  publishBlockRemedy?: { label: string; onClick: () => void } | null;
}

type WorkflowStepId = 'save' | 'promote' | 'publish' | 'sed';
type WorkflowStepState = 'complete' | 'current' | 'pending';

const STEP_ORDER: WorkflowStepId[] = ['save', 'promote', 'publish', 'sed'];

const stepCopy: Record<WorkflowStepId, { label: string; detail: string }> = {
  save: { label: 'Save', detail: 'Immutable scenario revision' },
  promote: { label: 'Promote', detail: 'Freeze into Project Intent' },
  publish: { label: 'Publish', detail: 'Versioned Site Map geometry' },
  sed: { label: 'Rebuild SED', detail: 'Freeze one authority tuple' },
};

function currentStep(code: TestFitWorkflowCode): WorkflowStepId | null {
  switch (code) {
    case 'scenario-required':
    case 'save-required':
    case 'scenario-conflict':
      return 'save';
    case 'feature-unavailable':
      return null;
    case 'promotion-required':
      return 'promote';
    case 'publication-required':
      return 'publish';
    case 'sed-rebuild-required':
    case 'sed-rebuilding':
    case 'sed-failed':
      return 'sed';
    case 'ready':
      return null;
  }
}

function stepState(step: WorkflowStepId, code: TestFitWorkflowCode): WorkflowStepState {
  if (code === 'ready') return 'complete';
  const current = currentStep(code);
  if (current === step) return 'current';
  if (current && STEP_ORDER.indexOf(step) < STEP_ORDER.indexOf(current)) return 'complete';
  return 'pending';
}

function WorkflowStep({ id, state }: { id: WorkflowStepId; state: WorkflowStepState }): React.JSX.Element {
  const copy = stepCopy[id];
  return (
    <li
      className={`min-w-0 border-l-2 px-3 py-2 ${
        state === 'complete'
          ? 'border-success bg-success/5'
          : state === 'current'
            ? 'border-primary bg-primary/5'
            : 'border-border bg-background'
      }`}
      data-testid={`test-fit-workflow-step-${id}`}
      data-state={state}
    >
      <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-text-primary">
        {state === 'complete'
          ? <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
          : <Circle className={`h-3.5 w-3.5 ${state === 'current' ? 'text-primary' : 'text-text-muted'}`} aria-hidden />}
        {copy.label}
      </span>
      <span className="mt-0.5 block text-[10px] text-text-muted">{copy.detail}</span>
    </li>
  );
}

export function GenerationImpactTestFitWorkflow({
  readiness,
  readOnly,
  busy,
  saving,
  promoting,
  publishing,
  onSave,
  onPromote,
  onReviewPublish,
  onRebuildSed,
  onGenerateTestFit,
  publishBlockReason = null,
  publishBlockRemedy = null,
}: Props): React.JSX.Element {
  const rebuilding = readiness.code === 'sed-rebuilding';
  // Explicit shape so `blockedReason` is readable on every branch, not just
  // the one that sets it (an inferred union would not expose it).
  interface WorkflowAction {
    label: string;
    icon: typeof Save;
    onClick: () => void;
    disabled: boolean;
    blockedReason?: string | null;
  }
  const action: WorkflowAction | null = (() => {
    switch (readiness.code) {
      case 'scenario-required':
      case 'save-required':
        return {
          label: saving ? 'Saving…' : 'Save scenario',
          icon: saving ? Loader2 : Save,
          onClick: onSave,
          disabled: readOnly || busy,
        };
      case 'scenario-conflict':
      case 'feature-unavailable':
        return null;
      case 'promotion-required':
        return {
          label: promoting ? 'Promoting…' : 'Promote to Project Intent',
          icon: promoting ? Loader2 : ShieldCheck,
          onClick: onPromote,
          disabled: readOnly || busy,
        };
      case 'publication-required':
        return {
          label: publishing ? 'Publishing…' : 'Review & publish layout',
          icon: publishing ? Loader2 : MapPinned,
          onClick: onReviewPublish,
          disabled: readOnly || busy || Boolean(publishBlockReason),
          blockedReason: publishBlockReason,
        };
      case 'sed-rebuild-required':
      case 'sed-failed':
        return {
          label: 'Rebuild SED',
          icon: FileCheck2,
          onClick: onRebuildSed,
          disabled: readOnly || busy,
        };
      case 'sed-rebuilding':
        return {
          label: 'Rebuilding SED…',
          icon: Loader2,
          onClick: onRebuildSed,
          disabled: true,
        };
      case 'ready':
        return onGenerateTestFit
          ? {
              label: 'Generate Test Fit',
              icon: FileCheck2,
              onClick: onGenerateTestFit,
              disabled: readOnly || busy,
            }
          : null;
    }
  })();
  const ActionIcon = action?.icon;

  return (
    <section
      className="border border-border bg-card rounded-md"
      aria-labelledby="generation-test-fit-workflow-heading"
      data-testid="generation-test-fit-workflow"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Version-safe handoff</p>
          <h2 id="generation-test-fit-workflow-heading" className="mt-1 text-sm font-bold text-text-primary">
            Prepare Test Fit
          </h2>
        </div>
        <span
          className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${
            readiness.ready
              ? 'border-success/40 bg-success/5 text-success'
              : rebuilding
                ? 'border-primary/40 bg-primary/5 text-primary'
                : 'border-warning/40 bg-warning/5 text-warning'
          } rounded-md`}
          data-testid="test-fit-workflow-status"
        >
          {readiness.ready ? 'Ready' : rebuilding ? 'Rebuilding' : 'Action required'}
        </span>
      </div>

      <ol className="grid gap-px bg-border p-px sm:grid-cols-2 lg:grid-cols-4" aria-label="Test Fit preparation steps">
        {STEP_ORDER.map((step) => <WorkflowStep key={step} id={step} state={stepState(step, readiness.code)} />)}
      </ol>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
        <div className="min-w-0 max-w-3xl">
          <strong className="block text-xs text-text-primary">{readiness.title}</strong>
          <p className="mt-0.5 text-[10px] leading-relaxed text-text-muted" data-testid="test-fit-workflow-message">
            {readiness.message}
          </p>
          {action?.blockedReason && (
            <p className="mt-1 text-[10px] font-bold leading-relaxed text-warning" data-testid="test-fit-workflow-block-reason" role="status">
              {action.blockedReason}
              {publishBlockRemedy && (
                <button
                  type="button"
                  onClick={publishBlockRemedy.onClick}
                  className="ml-2 font-bold uppercase tracking-wider text-primary underline-offset-2 hover:underline"
                  data-testid="test-fit-workflow-block-remedy"
                >
                  {publishBlockRemedy.label}
                </button>
              )}
            </p>
          )}
        </div>
        {action && ActionIcon && (
          <Button
            type="button"
            size="sm"
            onClick={action.onClick}
            disabled={action.disabled}
            data-testid="test-fit-workflow-action"
          >
            <ActionIcon className={`h-3.5 w-3.5 ${saving || promoting || publishing || rebuilding ? 'animate-spin' : ''}`} aria-hidden />
            {action.label}
          </Button>
        )}
      </div>
    </section>
  );
}
